/**
 * SofaFit - A tool to check if sofa dimensions will fit through doorways, hallways, and stairs/landings
 * @module SofaFit
 */

if (typeof SVG_DEFAULT_PATH == "undefined") {
    var SVG_DEFAULT_PATH = '/images/svg-symbols.svg';
}
if (typeof SVG_DEFAULT_PREFIX == "undefined") {
    var SVG_DEFAULT_PREFIX = 'icon';
}
function Svg() {

    this.imagePath = SVG_DEFAULT_PATH;

    this.imageClassPrefix = SVG_DEFAULT_PREFIX;

    this.setImagePath = function(path) {
        if (path) {
            this.imagePath = path;
        }
    }

    this.setImageClassPrefix = function(prefix) {
        if (prefix) {
            this.imageClassPrefix = prefix;
        }
    }

    this.render = function(icon) {
        return '<svg role="img" class="' + this.imageClassPrefix + '-' + this.prepare(icon) + '">' +
                        '<use xlink:href="' + this.imagePath + '#icon-' + this.prepare(icon) + '"></use>' +
                    '</svg>'
    }

    this.prepare = function(icon) {
        return icon.replace('icon-', '');
    }
}


/**
 * SofaFit - A tool to check if a sofa will fit through doors, hallways, and stairs
 * @class
 */
class SofaFit {
  /**
   * Create a new SofaFit instance
   */
  constructor() {
    this.svg = new Svg();
    this.selection = { frames: {} };
    this.user = { selectedFrame: false };
    this.homeDims = [];
    this.tooBig = {};
    this.diffs = [];
    this.count = 0;
    this.max = 0;
    this.noLanding = false;

    // Padding factors for stairs/landing measurements
    this.extra = {
      height: 1.09,
      depth: 1.19
    };

    // Initialize overlay when the script loads
    this.setUpOverlay();
  }

  /**
   * Initialize the SofaFit tool with all event listeners
   */
  init() {
    this.getElements();
    this.watchLandingCheckbox();
    this.watchSubmitClick();
    this.watchSubmit();
  }

  /**
   * Get necessary DOM elements and parse sofa dimensions
   */
  getElements() {
    const dimensionsElement = document.querySelector('.js-get-dimensions');
    if (dimensionsElement) {
      this.sofaDims = JSON.parse(dimensionsElement.dataset.dims);
    }
    this.resultsButton = document.querySelector('.js-get-results');
    this.step = document.querySelector('.pdp-popup')?.dataset.step;
    this.submitInputBtns = document.querySelectorAll('.js-submit-input');
  }

  /**
   * Setup the overlay system that displays the measurement steps
   */
  setUpOverlay() {
    const openSofaFitButton = document.querySelector('.js-open-sofa-fit');

    if (!openSofaFitButton) return;

    openSofaFitButton.addEventListener('click', () => {
      Overlay.render('#doors', {
        modifiers: ['sidebar', 'sofa-fit'],
        callback: () => {
          this.init();
        }
      });
    });
  }

  /**
   * Sets up the "I don't have a landing" checkbox behaviour
   */
  watchLandingCheckbox() {
    const overlayEl = Overlay.target;
    const noLandingCheckbox = overlayEl.querySelector('input[type="checkbox"]');

    if (!noLandingCheckbox) return;

    noLandingCheckbox.addEventListener('click', (event) => {
      const inputs = Array.from(
        overlayEl.querySelectorAll('input[type="text"]')
      );

      inputs.forEach(input => {
        input.disabled = event.currentTarget.checked;
      });

      this.noLanding = event.currentTarget.checked;
    });
  }

  /**
   * Set up click handling for "next step" buttons
   */
  watchSubmitClick() {
    if (!this.submitInputBtns) return;

    this.submitInputBtns.forEach(submitInputBtn => {
      submitInputBtn.addEventListener('click', () => {
        const overlayEl = Overlay.target;
        const inputs = Array.from(
          overlayEl.querySelectorAll('input[type="text"]')
        );
        const step = overlayEl.querySelector('.pdp-popup').dataset.step;
        const nextPopup = overlayEl.querySelector('.pdp-popup').dataset.nextPopup;

        // Verify all inputs are valid numbers
        const validInputs = inputs.every(
          input => !isNaN(parseInt(input.value))
        );

        // Special case for stairs with "no landing" checked
        const isValidLandingStep = step === 'stairs' && this.noLanding;

        if (validInputs || isValidLandingStep) {
          // Create an object from the input values
          const inputValues = inputs.map(input => [
            input.name,
            parseInt(input.value)
          ]);
          
          const dimensions = Object.fromEntries([...inputValues, ['step', step]]);
          
          // Process and store the dimensions
          this.processDimensions(dimensions);

          // If we're at the final step, calculate results
          if (this.count === 2) {
            this.calculateResults();
          }

          // Move to the next step
          Overlay.render(`#${nextPopup}`, {
            modifiers: ['sidebar', 'sofa-fit'],
            callback: () => {
              this.init();

              if (Overlay.target.querySelector('.js-get-dimensions')) {
                this.insertTableValues();
                this.initEditDimensions();
              }
            }
          });

          // Only increment the counter for valid inputs or valid landing step
          if (validInputs || isValidLandingStep) {
            this.count++;
          }
        } else {
          this.invalidInputs();
        }
      });
    });
  }

  /**
   * Process and store dimensions from the form
   * @param {Object} dimensions - The dimensions object with values from inputs
   */
  processDimensions(dimensions) {
    // Check if we're editing an existing step
    let existingDimIndex = -1;
    
    this.homeDims.forEach((obj, index) => {
      if (obj.step === dimensions.step) {
        existingDimIndex = index;
      }
    });

    // For the stairs/landing step with "no landing" checked,
    // ensure that we don't store NaN values
    if (dimensions.step === 'stairs' && this.noLanding) {
      // If user selected "no landing", we don't need exact dimensions
      // so we can safely set them to empty values
      if (dimensions.width === undefined || isNaN(dimensions.width)) dimensions.width = '';
      if (dimensions.depth === undefined || isNaN(dimensions.depth)) dimensions.depth = '';
      if (dimensions.height === undefined || isNaN(dimensions.height)) dimensions.height = '';
    }

    if (existingDimIndex >= 0) {
      // Update existing dimensions
      this.homeDims[existingDimIndex] = dimensions;
    } else {
      // Add new dimensions
      this.homeDims.push(dimensions);
    }

    // Clear existing calculation results for this step
    this.sofaDims.forEach(size => {
      size.diffs = {};
      this.tooBig[size.label] = this.tooBig[size.label] || {};

      // Check if dimensions fit based on the current step
      this.checkDimensionsFit(size, dimensions);
    });
  }

  /**
   * Check if a sofa's dimensions fit through the current step (door, hallway, or stairs)
   * @param {Object} size - The sofa dimensions
   * @param {Object} dimensions - The user's home dimensions
   */
  checkDimensionsFit(size, dimensions) {
    const stepType = dimensions.step;
    
    let fitResult = true; // Default: it fits

    if (stepType === 'doors') {
      fitResult = this.checkDoorFit(size, dimensions);
    } else if (stepType === 'hallway') {
      fitResult = this.checkHallwayFit(size, dimensions);
    } else if (stepType === 'stairs' && !this.noLanding) {
      fitResult = this.checkStairsLandingFit(size, dimensions);
    }

    // If it doesn't fit, store the results
    if (fitResult !== true) {
      this.tooBig[size.label][dimensions.step] = fitResult;
    }
  }

  /**
   * Calculate the final results for display
   */
  calculateResults() {
    const results = document.querySelectorAll('.js-popup-results');
    
    results.forEach(result => {
      const label = result.getAttribute('data-label');
      const diffs = [];

      if (this.tooBig[label]) {
        const data = this.tooBig[label];

        // Collect all dimension differences
        Object.keys(data).forEach(key => {
          const item = data[key];
          
          if (item.widthDiff) diffs.push(item.widthDiff);
          if (item.heightDiff) diffs.push(item.heightDiff);
          if (item.depthDiff) diffs.push(item.depthDiff);
        });

        if (diffs.length) {
          // Find the maximum difference
          const max = Math.max(...diffs.filter(item => !isNaN(Number(item))));

          if (max > 0) {
            result.innerHTML = `will not fit and is ${max}cm over`;
            this.activateRadioState(
              document.querySelector(`.js-popup-radio[data-label="${label}"]`),
              false
            );
          } else {
            result.innerHTML = `will fit`;
            this.activateRadioState(
              document.querySelector(`.js-popup-radio[data-label="${label}"]`),
              true
            );
          }
        } else {
          result.innerHTML = `will fit`;
          this.activateRadioState(
            document.querySelector(`.js-popup-radio[data-label="${label}"]`),
            true
          );
        }
      } else {
        result.innerHTML = `will fit`;
        this.activateRadioState(
          document.querySelector(`.js-popup-radio[data-label="${label}"]`),
          true
        );
      }
    });
  }

  /**
   * Mark inputs as invalid
   */
  invalidInputs() {
    const overlayEl = Overlay.target;
    const inputs = Array.from(
      overlayEl.querySelectorAll('input[type="text"]')
    );

    inputs.forEach(input => {
      input.classList.add('invalid');
    });
  }

  /**
   * Update the visual state of radio buttons
   * @param {HTMLElement} radio - The radio button element
   * @param {boolean} willFit - Whether the sofa will fit
   */
  activateRadioState(radio, willFit) {
    if (!radio) return;
    
    // Remove existing state classes
    radio.classList.remove('custom-radio__icon--tick', 'custom-radio__icon--cross');
    
    // Add appropriate class based on fit status
    radio.classList.add(willFit ? 'custom-radio__icon--tick' : 'custom-radio__icon--cross');
  }

  /**
   * Initialize the edit dimension buttons
   */
  initEditDimensions() {
    const editButtons = Overlay.target.querySelectorAll('.js-edit-dimensions');

    for (const button of editButtons) {
      button.addEventListener('click', event => {
        event.preventDefault();

        const overlayURL = event.currentTarget.id;
        this.count = parseInt(event.currentTarget.dataset.count);
        
        // Reset calculation variables for recalculation
        this.diffs = [];
        this.tooBig = {};

        Overlay.render(`#${overlayURL}`, {
          modifiers: ['sidebar', 'sofa-fit'],
          callback: () => {
            this.init();

            // If editing stairs/landing, restore checkbox state
            if (overlayURL === 'stairs' && this.noLanding) {
              const noLandingCheckbox = Overlay.target.querySelector('input[type="checkbox"]');
              if (noLandingCheckbox) {
                noLandingCheckbox.checked = true;
                
                // Disable the input fields
                const inputs = Array.from(
                  Overlay.target.querySelectorAll('input[type="text"]')
                );
                inputs.forEach(input => {
                  input.disabled = true;
                });
              }
            }
            
            // Pre-fill input values from saved data
            if (this.homeDims[this.count]) {
              const inputs = Array.from(
                Overlay.target.querySelectorAll('input[type="text"]')
              );
              
              inputs.forEach(input => {
                const fieldName = input.getAttribute('name');
                if (this.homeDims[this.count][fieldName] !== undefined) {
                  // Only set the value if it's a valid number, otherwise leave it blank
                  const value = this.homeDims[this.count][fieldName];
                  input.value = !isNaN(value) ? value : '';
                }
              });
            }

            if (Overlay.target.querySelector('.js-get-dimensions')) {
              this.insertTableValues();
              this.initEditDimensions();
            }
          }
        });
      });
    }
  }

  /**
   * Handle form submission to update the sofa selection
   */
  watchSubmit() {
    $('.js-radio-form').on('submit', event => {
      event.preventDefault();

      const selectedFrame = $('.js-sofa-fit-frame:checked').val();
      const frameKey = `frames[${selectedFrame}]`;

      AndrewMartin.customise.updateCombinationQty(selectedFrame, 1, true);
      
      if (this.user.selectedFrame) {
        AndrewMartin.customise.replaceUrl(frameKey, 1);
      } else {
        AndrewMartin.customise.updateUrl(frameKey, 1);
      }
      
      const form = $(event.currentTarget);

      $.ajax(window.location.href, {
        type: 'post',
        data: {
          formData: form.serialize()
        },
        success: data => {
          this.user.selectedFrame = true;
          AndrewMartin.customise.refreshAfterAjax(data);
          Overlay.close();
        },
        fail: () => {
          Overlay.close();
        }
      });
    });
  }

  /**
   * Insert the dimension values into the summary table
   */
  insertTableValues() {
    const overlayEl = Overlay.target;
    const doorTable = overlayEl.querySelector('.js-wmsf-door');
    const hallwayTable = overlayEl.querySelector('.js-wmsf-hallway');
    const stairsTable = overlayEl.querySelector('.js-wmsf-stairs');

    if (this.homeDims[0]) {
      doorTable.textContent = `${this.homeDims[0].width} (W) x ${this.homeDims[0].height} (H) cm`;
    }
    
    if (this.homeDims[1]) {
      hallwayTable.textContent = `${this.homeDims[1].width} (W) cm`;
    }
    
    if (this.homeDims[2]) {
      if (!this.noLanding) {
        stairsTable.textContent = `${this.homeDims[2].width} (W) x ${this.homeDims[2].depth} (D) x ${this.homeDims[2].height} (H) cm`;
      } else {
        stairsTable.textContent = `I don't have a landing`;
      }
    }
  }

  /**
   * Check if furniture fits through a door in any orientation
   * @param {Object} size - The sofa dimensions
   * @param {Object} doorDims - The door dimensions
   * @returns {boolean|Object} - true if it fits, or an object with the difference
   */
  checkDoorFit(size, doorDims) {
    // A piece fits through a door if its smallest two dimensions can pass through
    // For a door opening, we need two dimensions of the sofa to be <= door dimensions
    
    // Make sure we have all dimensions
    const sofaHeight = size.height || 0;
    const sofaWidth = size.width || 0;
    const sofaDepth = size.depth || 0;
    const doorWidth = doorDims.width || 0;
    const doorHeight = doorDims.height || 0;
    
    // Check all possible orientations - we need TWO dimensions to fit through door
    
    // Orientation 1: sofa on its side (height through door width, width OR depth through door height)
    if (sofaHeight <= doorWidth && (sofaWidth <= doorHeight || sofaDepth <= doorHeight)) {
      return true;
    }
    
    // Orientation 2: sofa upright (width through door width, height OR depth through door height)
    if (sofaWidth <= doorWidth && (sofaHeight <= doorHeight || sofaDepth <= doorHeight)) {
      return true;
    }
    
    // Orientation 3: sofa flat (depth through door width, height OR width through door height)
    if (sofaDepth <= doorWidth && (sofaHeight <= doorHeight || sofaWidth <= doorHeight)) {
      return true;
    }
    
    // If we get here, furniture won't fit, so calculate smallest amount over
    
    // Find the smallest dimension that needs to go through door width
    const bestWidthDimension = Math.min(
      sofaHeight, 
      sofaWidth, 
      sofaDepth
    );
    
    // How much over the door width is the smallest dimension?
    let widthDiff = Math.max(0, bestWidthDimension - doorWidth);
    
    // If the smallest dimension fits through door width, no width diff
    if (bestWidthDimension <= doorWidth) {
      widthDiff = 0;
    }
    
    // For each possible orientation, check the other two dimensions against door height
    let heightDiff = Infinity;
    
    // If height could go through door width, check if width OR depth fit through height
    if (sofaHeight <= doorWidth) {
      heightDiff = Math.min(
        heightDiff,
        Math.max(0, Math.min(sofaWidth, sofaDepth) - doorHeight)
      );
    }
    
    // If width could go through door width, check if height OR depth fit through height
    if (sofaWidth <= doorWidth) {
      heightDiff = Math.min(
        heightDiff,
        Math.max(0, Math.min(sofaHeight, sofaDepth) - doorHeight)
      );
    }
    
    // If depth could go through door width, check if height OR width fit through height
    if (sofaDepth <= doorWidth) {
      heightDiff = Math.min(
        heightDiff,
        Math.max(0, Math.min(sofaHeight, sofaWidth) - doorHeight)
      );
    }
    
    // If we couldn't find any valid orientation where at least one dimension fits
    // through door width, height diff remains Infinity - set it to 0 in that case
    if (heightDiff === Infinity) {
      heightDiff = 0;
    }
    
    // Use only non-zero diff if possible
    const effectiveDiff = (widthDiff > 0) ? widthDiff : (heightDiff > 0 ? heightDiff : 0);
    
    return { widthDiff: effectiveDiff };
  }

  /**
   * Check if furniture fits through a hallway
   * @param {Object} size - The sofa dimensions
   * @param {Object} hallwayDims - The hallway dimensions
   * @returns {boolean|Object} - true if it fits, or an object with the difference
   */
  checkHallwayFit(size, hallwayDims) {
    // For a hallway, we only have width measurement
    // The sofa needs to have its smallest dimension less than the hallway width
    const sofaHeight = size.height || 0;
    const sofaWidth = size.width || 0;
    const sofaDepth = size.depth || 0;
    const hallwayWidth = hallwayDims.width || 0;
    
    // Find the smallest dimension of the sofa
    const smallestDimension = Math.min(sofaHeight, sofaWidth, sofaDepth);
    
    // If the smallest dimension fits through the hallway width, it can be maneuvered
    if (smallestDimension <= hallwayWidth) {
      return true;
    }
    
    // If it doesn't fit, return how much over it is
    return { widthDiff: smallestDimension - hallwayWidth };
  }

  /**
   * Check if furniture fits through stairs/landing
   * @param {Object} size - The sofa dimensions
   * @param {Object} landingDims - The landing dimensions
   * @returns {boolean|Object} - true if it fits, or an object with the differences
   */
  checkStairsLandingFit(size, landingDims) {
    // For stairs/landing, we check if the sofa can be maneuvered in the 3D space
    const sofaHeight = size.height || 0;
    const sofaWidth = size.width || 0;
    const sofaDepth = size.depth || 0;
    
    // Apply the extra padding factors for stairs/landing
    const adjustedHeight = sofaHeight * this.extra.height;
    const adjustedDepth = sofaDepth * this.extra.depth;
    
    const landingWidth = landingDims.width || 0;
    const landingDepth = landingDims.depth || 0;
    const landingHeight = landingDims.height || 0;
    
    // Check various orientations for the landing
    // 1. Width fits through width, depth through depth, height through height
    if (sofaWidth <= landingWidth && adjustedDepth <= landingDepth && adjustedHeight <= landingHeight) {
      return true;
    }
    
    // 2. Height fits through width, width through depth, depth through height
    if (sofaHeight <= landingWidth && sofaWidth <= landingDepth && adjustedDepth <= landingHeight) {
      return true;
    }
    
    // 3. Depth fits through width, height through depth, width through height
    if (sofaDepth <= landingWidth && sofaHeight <= landingDepth && sofaWidth <= landingHeight) {
      return true;
    }
    
    // Calculate differences for each dimension
    const widthDiff = Math.max(0, sofaWidth - landingWidth);
    const depthDiff = Math.max(0, adjustedDepth - landingDepth);
    const heightDiff = Math.max(0, adjustedHeight - landingHeight);
    
    // Return the dimensions that are over
    return { 
      widthDiff: widthDiff > 0 ? widthDiff : 0,
      depthDiff: depthDiff > 0 ? depthDiff : 0,
      heightDiff: heightDiff > 0 ? heightDiff : 0
    };
  }
}

// Initialize the global namespace if not already existing
var AndrewMartin = AndrewMartin || {};

// Create a new instance and set up the overlay
AndrewMartin.sofaFit = new SofaFit();
